import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { gsap } from "@repo/utils/gsap"
import { useRouter } from "next/router"
import { useNavigation } from "../providers/NavigationProvider"

const SMALL_MENU_THRESHOLD = 90

export default function useHideTopBarOnScroll() {
	const { asPath } = useRouter()
	const [isMenuSmall, setIsMenuSmall] = useState(false)

	const transitionCompleted = useNavigation((state) => state.isTransitionCompleted)

	const [isVisible, setIsVisible] = useState(true)
	const [isInitialRender, setIsInitialRender] = useState(true)

	const scrollYRef = useRef(0)
	const isMenuOpen = useSelector((state) => state.menu.open)

	const topRowElRef = useRef(null)
	const stickyHeadingRef = useRef(null)

	useEffect(() => {
		function handleScroll() {
			const isStorySite = asPath.split("/").length === 2 && asPath.includes("story")
			const currentScrollY = window.scrollY
			const viewportHeight = window.innerHeight
			const documentHeight = document.documentElement.scrollHeight
			const bottomOffset = documentHeight - (currentScrollY + viewportHeight)
			const isScrollingUp = currentScrollY < scrollYRef.current
			const condition1 = isScrollingUp || currentScrollY < 10
			const condition2 = isStorySite ? true : bottomOffset > 60
			const shouldShow = condition1 && condition2

			//setters
			setIsMenuSmall(currentScrollY > SMALL_MENU_THRESHOLD)
			setIsVisible(shouldShow)

			scrollYRef.current = currentScrollY
		}

		const throttledHandleScroll = throttle(handleScroll, 100)

		const controller = new AbortController()
		window.addEventListener("scroll", throttledHandleScroll, { signal: controller.signal })

		return () => {
			controller.abort()
		}
	}, [asPath])

	useEffect(() => {
		topRowElRef.current = document.getElementById("menu-top-row")
		stickyHeadingRef.current = document.getElementById("sticky-heading")
	}, [isInitialRender, asPath])

	useEffect(() => {
		setIsInitialRender(false)
	}, [])

	useEffect(() => {
		if (isInitialRender || !topRowElRef.current || !transitionCompleted) return

		const animationDuration = 0.1

		if (isVisible || isMenuOpen) {
			gsap.to(topRowElRef.current, {
				ease: "none",
				duration: animationDuration,
				y: "0",
			})
			if (stickyHeadingRef.current) {
				stickyHeadingRef.current.style.opacity = "0"
			}
		} else {
			gsap.to(topRowElRef.current, {
				ease: "none",
				duration: animationDuration,
				y: "-120",
			})
			if (stickyHeadingRef.current) {
				stickyHeadingRef.current.style.opacity = "1"
			}
		}
	}, [isVisible, isMenuOpen, isInitialRender, transitionCompleted])

	return {
		isMenuSmall,
	}
}

function throttle(func, delay) {
	let lastCall = 0
	return function (...args) {
		const now = new Date().getTime()
		if (now - lastCall < delay) {
			return
		}
		lastCall = now
		return func(...args)
	}
}
